import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  ExclamationIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/solid";
import {
  BUSINESS_STATUS,
  getBusinessStatus,
  getBusinessLifecycle,
} from "../../../../Constants/business";

const ICONS = {
  INACTIVE_WARNED: ClockIcon,
  RESTRICTED: ExclamationIcon,
  PENDING_DELETION: ExclamationCircleIcon,
};

/**
 * Small pill showing a business lifecycle status. Renders nothing for ACTIVE
 * (the default, healthy state) to avoid badge noise.
 *
 * The pill never wraps and never pushes the business name around: it prints the
 * short label, keeps a status icon so the meaning survives at a glance (and for
 * colour-blind users, who can't lean on red-vs-amber alone), and carries the
 * full phrase plus the concrete deadline in the tooltip / accessible name.
 *
 * Props:
 *  - business / status: the record, or a bare status value.
 *  - withDeadline: when the lifecycle has a known deadline, append the days-left
 *      to the tooltip ("Scheduled for deletion · 12 days left").
 *  - className: layout context (spacing) only.
 */
const BusinessStatusBadge = ({
  business,
  status,
  withDeadline = true,
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const value = status || getBusinessStatus(business);

  if (!value || value === "ACTIVE") return null;

  const config = BUSINESS_STATUS[value] || BUSINESS_STATUS.ACTIVE;
  const Icon = ICONS[value];

  // Tooltip: the full phrase, plus the deadline when we know it. Days-left is
  // the number the user actually acts on, so it leads; the date disambiguates.
  let tooltip = t(config.label);
  if (withDeadline && business) {
    const life = getBusinessLifecycle(business);
    if (life?.deadline) {
      const date = dayjs(life.deadline)
        .locale(i18n.language)
        .format("DD MMM YYYY");
      const unit = life.daysLeft === 1 ? t("day") : t("days");
      tooltip =
        life.daysLeft != null
          ? `${tooltip} · ${life.daysLeft} ${unit} (${date})`
          : `${tooltip} · ${date}`;
    }
  }

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${config.badgeClass} ${className}`}
      title={tooltip}
      aria-label={tooltip}
    >
      {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
      <span className="truncate">{t(config.shortLabel)}</span>
    </span>
  );
};

export default BusinessStatusBadge;
